import { AlertBS, AlertRootProps } from "@biblioteksentralen/react";
import InternalLink from "../../../components/InternalLink";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { getDocumentTypeDefiniteNoun } from "../../cordata/documentTypes";
import { useManifestationStatusForUser } from "./useManifestationStatusForUser";
import { useRediaPlatformContext } from "../../../rediaPlatform/RediaPlatformProvider";
import { LCWorkTriple, VariantWithTriples } from "@libry-content/integrations";
import { isReservationDisabled } from "../reservation/isReservationDisabled";

type ManifestationStatusForUserProps = Omit<AlertRootProps, "status"> & {
  representativeWorkTriple: LCWorkTriple;
  variantWithTriples?: VariantWithTriples;
};

export const ManifestationStatusForUser = ({
  variantWithTriples,
  representativeWorkTriple,
  ...alertProps
}: ManifestationStatusForUserProps) => {
  const { t, ts } = useTranslation();
  const { rediaPlatformIsEnabled } = useRediaPlatformContext();
  const variantWithTriplesToUse = variantWithTriples ?? representativeWorkTriple.variantWithTriples;
  const manifestationStatusForUser = useManifestationStatusForUser(variantWithTriplesToUse?.workTriples ?? []);
  if (!manifestationStatusForUser || !rediaPlatformIsEnabled) return null;
  const documentType = variantWithTriples?.variant.documentType;

  const reservationIsDisabled = isReservationDisabled({
    workTriple: representativeWorkTriple,
    variant: variantWithTriples,
    manifestationStatusForUser,
  });

  const { isLoanedByUser, isReservedByUser, error } = manifestationStatusForUser;

  if (error || !reservationIsDisabled) return null;

  if (isLoanedByUser) {
    return (
      <StatusAlertWrapper
        header={t("Du har lånt {materialet}.", {
          materialet: ts(getDocumentTypeDefiniteNoun(documentType, { withDeterminative: true })),
        })}
        {...alertProps}
      >
        <InternalLink display="block" marginBottom="0.75rem" href="/min-side/laan">
          {t("Se mine lån")}
        </InternalLink>
      </StatusAlertWrapper>
    );
  }

  if (isReservedByUser) {
    return (
      <StatusAlertWrapper
        header={t("Du har reservert {materialet}.", {
          materialet: ts(getDocumentTypeDefiniteNoun(documentType, { withDeterminative: true })),
        })}
        {...alertProps}
      >
        <InternalLink display="block" href="/min-side/reservasjon">
          {t("Se mine reservasjoner")}
        </InternalLink>
      </StatusAlertWrapper>
    );
  }

  return null;
};

const StatusAlertWrapper = ({
  header,
  children,
  ...alertProps
}: { header: string } & Omit<AlertRootProps, "status">) => (
  <AlertBS
    header={header}
    status="warning"
    textAlign="left"
    gap="0.5rem"
    padding="0.5rem"
    fontSize="md"
    {...alertProps}
  >
    {children}
  </AlertBS>
);
